import { useContext, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { deleteContrato } from "../../../../application/contratos";
import { formatMonto, diasHastaFecha, estadoVencimiento, formatFechaCorta } from "../../../../utils/rentalCalculations";
import { getContratoEstadoClasses, getVencimientoClasses } from "../../../../utils/rentalStatusColors";
import { FaPlusCircle, FaEdit, FaTrash, FaSearch, FaFilePdf, FaFileWord } from "react-icons/fa";
import { esWord } from "../../../../application/contratoFiles";
import DocumentoViewer from "../../../../components/DocumentoViewer";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";
import { nombreCompleto } from "../../../../utils/nombre";
import { agruparPorClave } from "../../../../utils/agrupar";

function ContratoList() {
  const { contratos, locadores, locatarios, refreshContratos, config } = useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);
  const [search, setSearch] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [filtroMoneda, setFiltroMoneda] = useState("TODAS");
  const [agrupar, setAgrupar] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [docViewer, setDocViewer] = useState(null);

  // Algunos contratos guardaron locador/locatarioNombre vacío aunque tengan el id.
  // Resolvemos el nombre desde las listas por id como respaldo.
  const contratosEnriquecidos = useMemo(() => {
    const nombrePorId = (lista) => {
      const m = {};
      lista.forEach((p) => { if (p.idFirebase) m[p.idFirebase] = nombreCompleto(p); });
      return m;
    };
    const locadorMap = nombrePorId(locadores);
    const locatarioMap = nombrePorId(locatarios);
    return contratos.map((c) => ({
      ...c,
      locadorNombre: c.locadorNombre || locadorMap[c.locadorId] || "",
      locatarioNombre: c.locatarioNombre || locatarioMap[c.locatarioId] || "",
    }));
  }, [contratos, locadores, locatarios]);

  const filtered = useMemo(() => {
    return contratosEnriquecidos.filter((c) => {
      const matchSearch =
        !search ||
        c.locadorNombre?.toLowerCase().includes(search.toLowerCase()) ||
        c.locatarioNombre?.toLowerCase().includes(search.toLowerCase()) ||
        c.propiedad?.toLowerCase().includes(search.toLowerCase());
      const matchEstado = filtroEstado === "TODOS" || c.estado === filtroEstado;
      const matchMoneda = filtroMoneda === "TODAS" || c.moneda === filtroMoneda;
      return matchSearch && matchEstado && matchMoneda;
    });
  }, [contratosEnriquecidos, search, filtroEstado, filtroMoneda]);

  const getValue = useCallback((c, key) => {
    if (key === "proximoAumento") return c.aumento?.proximoAumento;
    return c?.[key];
  }, []);
  const { sortedData, sortConfig, requestSort } = useSortableData(filtered, {
    getValue,
    storageKey: "sort-contratos",
    initial: { key: "fechaVencimiento", direction: "asc" },
  });

  // Filas a renderizar. Con agrupado, intercala una fila de encabezado por
  // locador antes de sus contratos.
  const filasRender = useMemo(() => {
    if (!agrupar) return sortedData.map((c) => ({ tipo: "fila", c }));
    const grupos = agruparPorClave(sortedData, (c) => c.locadorNombre);
    const out = [];
    for (const [locador, items] of grupos) {
      out.push({
        tipo: "grupo",
        locador,
        items,
        subtotalMonto: items.reduce((s, x) => s + (Number(x.montoActual) || 0), 0),
      });
      items.forEach((c) => out.push({ tipo: "fila", c }));
    }
    return out;
  }, [agrupar, sortedData]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este contrato?")) return;
    const contrato = contratosEnriquecidos.find((c) => c.idFirebase === id);
    setDeleting(id);
    try {
      await deleteContrato(id);
      await refreshContratos();
      if (contrato) {
        registrarActividad("CONTRATO_ELIMINADO", {
          mensaje: `Contrato eliminado: ${contrato.locatarioNombre} - ${contrato.propiedad}`,
          entidadTipo: "CONTRATO",
          entidadLabel: contrato.propiedad,
        });
      }
    } catch (error) {
      console.error("Error al eliminar contrato:", error);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Contratos
        </h1>
        <Link
          to="/dashboard/alquileres/contratos/nuevo"
          className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <FaPlusCircle className="w-4 h-4" /> Nuevo Contrato
        </Link>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por locador, locatario o propiedad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="w-full sm:w-auto text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            <option value="TODOS">Todos los estados</option>
            <option value="ACTIVO">Activo</option>
            <option value="VENCIDO">Vencido</option>
            <option value="RESCINDIDO">Rescindido</option>
          </select>
          <select
            value={filtroMoneda}
            onChange={(e) => setFiltroMoneda(e.target.value)}
            className="w-full sm:w-auto text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          >
            <option value="TODAS">Todas las monedas</option>
            <option value="ARS">ARS</option>
            <option value="USD">USD</option>
          </select>
          <button
            onClick={() => setAgrupar((v) => !v)}
            className={`col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-2 text-sm border font-medium px-3 py-2 rounded-lg transition-colors whitespace-nowrap ${
              agrupar
                ? "bg-primary/10 border-primary text-primary"
                : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
            title="Agrupar los contratos por locador"
          >
            {agrupar ? "Desagrupar" : "Agrupar por locador"}
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <SortableHeader sortKey="locadorNombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Locador</SortableHeader>
                <SortableHeader sortKey="locatarioNombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Locatario</SortableHeader>
                <SortableHeader sortKey="propiedad" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Propiedad</SortableHeader>
                <SortableHeader sortKey="montoActual" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Monto</SortableHeader>
                <SortableHeader sortKey="comisionMonto" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Comision</SortableHeader>
                <SortableHeader sortKey="fechaVencimiento" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Vencimiento</SortableHeader>
                <SortableHeader sortKey="proximoAumento" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Aumento</SortableHeader>
                <SortableHeader sortKey="estado" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Estado</SortableHeader>
                <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                    No se encontraron contratos
                  </td>
                </tr>
              ) : (
                filasRender.map((f) => {
                  if (f.tipo === "grupo") {
                    return (
                      <tr key={`grupo-${f.locador}`} className="bg-gray-100 dark:bg-gray-700/50 border-y border-gray-200 dark:border-gray-600">
                        <td colSpan={9} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {f.locador || "—"}
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            {f.items.length} contrato{f.items.length === 1 ? "" : "s"} · {formatMonto(f.subtotalMonto)}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  const c = f.c;
                  const vencStatus = estadoVencimiento(c.fechaVencimiento, config.diasAlertaVencimiento);
                  const diasVenc = diasHastaFecha(c.fechaVencimiento);
                  const diasAumento = diasHastaFecha(c.aumento?.proximoAumento);
                  return (
                    <tr
                      key={c.idFirebase}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                        vencStatus === "POR_VENCER" ? "bg-amber-50/50 dark:bg-amber-900/5" : ""
                      }`}
                    >
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{c.locadorNombre}</td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{c.locatarioNombre}</td>
                      <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{c.propiedad}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-800 dark:text-gray-200">
                        {formatMonto(c.montoActual, c.moneda)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                        {formatMonto(c.comisionMonto, c.moneda)}
                      </td>
                      <td className="px-4 py-3 text-center text-xs whitespace-nowrap">
                        {c.fechaVencimiento ? (
                          <div>
                            <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${getVencimientoClasses(vencStatus)}`}>
                              {formatFechaCorta(c.fechaVencimiento)}
                            </span>
                            {diasVenc !== null && (
                              <span className="block text-[10px] text-gray-400 mt-0.5">
                                {diasVenc < 0 ? `Vencido hace ${Math.abs(diasVenc)}d` : `En ${diasVenc}d`}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-xs whitespace-nowrap">
                        {c.aumento?.proximoAumento ? (
                          <div>
                            <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${
                              diasAumento !== null && diasAumento <= 30
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700/30 dark:text-gray-400"
                            }`}>
                              {formatFechaCorta(c.aumento.proximoAumento)}
                            </span>
                            {diasAumento !== null && (
                              <span className="block text-[10px] text-gray-400 mt-0.5">
                                {diasAumento < 0 ? `Hace ${Math.abs(diasAumento)}d` : `En ${diasAumento}d`}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getContratoEstadoClasses(c.estado)}`}>
                          {c.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {c.pdfContrato?.url ? (
                            <button
                              type="button"
                              onClick={() => setDocViewer(c.pdfContrato)}
                              className={`transition-colors ${esWord(c.pdfContrato) ? "text-blue-500 hover:text-blue-700" : "text-red-400 hover:text-red-600"}`}
                              title={`Ver documento (${c.pdfContrato.name || "contrato"})`}
                            >
                              {esWord(c.pdfContrato) ? <FaFileWord className="w-4 h-4" /> : <FaFilePdf className="w-4 h-4" />}
                            </button>
                          ) : (
                            <span className="text-gray-200 dark:text-gray-700" title="Sin documento cargado">
                              <FaFilePdf className="w-4 h-4" />
                            </span>
                          )}
                          <Link
                            to={`/dashboard/alquileres/contratos/editar/${c.idFirebase}`}
                            className="text-gray-400 hover:text-primary transition-colors"
                            title="Editar"
                          >
                            <FaEdit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDelete(c.idFirebase)}
                            disabled={deleting === c.idFirebase}
                            className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            title="Eliminar"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400 text-right">
        {filtered.length} de {contratos.length} contratos
      </p>

      {docViewer && <DocumentoViewer meta={docViewer} onClose={() => setDocViewer(null)} />}
    </div>
  );
}

export default ContratoList;
